import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography
} from "@material-ui/core";
import {
	MoreVert,
	Replay,
	PauseCircleOutline,
	PlayArrow,
	CheckCircleOutline,
	Phone,
	PhoneDisabled,
	PhoneInTalk,
	Forum
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 8,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		padding: "4px 6px",
		minHeight: 40,
		borderRadius: 999,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(15, 23, 42, 0.34)"
				: "rgba(15, 23, 42, 0.03)",
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.24)"
				: "rgba(148, 163, 184, 0.26)"
		}`,
	},
	softButton: {
		borderRadius: 999,
		padding: "5px 10px",
		minHeight: 32,
		fontWeight: 700,
		letterSpacing: 0,
		textTransform: "none",
		color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
		borderColor:
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.28)"
				: "rgba(148, 163, 184, 0.36)",
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(30, 41, 59, 0.38)"
				: "rgba(255, 255, 255, 0.78)",
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(51, 65, 85, 0.45)"
					: "rgba(241, 245, 249, 0.95)"
		}
	},
	actionIconButton: {
		width: 32,
		height: 32,
		borderRadius: 999,
		color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.28)"
				: "rgba(148, 163, 184, 0.36)"
		}`,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(30, 41, 59, 0.38)"
				: "rgba(255, 255, 255, 0.78)",
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(51, 65, 85, 0.45)"
					: "rgba(241, 245, 249, 0.95)"
		}
	},
	actionIconButtonPrimary: {
		width: 32,
		height: 32,
		borderRadius: 999,
		color: "#ffffff",
		background:
			theme.palette.type === "dark"
				? "linear-gradient(180deg, #2aa092 0%, #176b63 100%)"
				: "linear-gradient(180deg, #22877d 0%, #176b63 100%)",
		border: "none",
		"&:hover": {
			background:
				theme.palette.type === "dark"
			? "linear-gradient(180deg, #34b5a5 0%, #1e7d72 100%)"
					: "linear-gradient(180deg, #299488 0%, #1b776d 100%)"
		}
	},
	wabaPermissionStatusCard: {
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
		padding: theme.spacing(1.5),
		borderRadius: 12,
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.22)"
				: "rgba(148, 163, 184, 0.32)"
		}`,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(15, 23, 42, 0.32)"
				: "rgba(248, 250, 252, 0.92)"
	},
	wabaPermissionStatusHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1)
	},
	wabaPermissionBadge: {
		display: "inline-flex",
		alignItems: "center",
		borderRadius: 999,
		padding: "3px 9px",
		fontSize: 11,
		fontWeight: 800,
		letterSpacing: 0.2,
		textTransform: "uppercase"
	},
	wabaPermissionBadgeSuccess: {
		color: "#065f46",
		backgroundColor: "#d1fae5"
	},
	wabaPermissionBadgeWarning: {
		color: "#92400e",
		backgroundColor: "#fef3c7"
	},
	wabaPermissionBadgeDanger: {
		color: "#991b1b",
		backgroundColor: "#fee2e2"
	},
	wabaPermissionBadgeNeutral: {
		color: "#334155",
		backgroundColor: "#e2e8f0"
	},
	wabaPermissionMetaRow: {
		display: "flex",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: "5px 0",
		borderTop: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(148, 163, 184, 0.14)"
				: "rgba(148, 163, 184, 0.22)"
		}`,
		"&:first-of-type": {
			borderTop: 0
		}
	},
	resolveButton: {
		borderRadius: 999,
		padding: "5px 12px",
		minHeight: 32,
		fontWeight: 700,
		letterSpacing: 0,
		textTransform: "none",
		boxShadow: "none",
		"&:hover": {
			boxShadow: "none"
		}
	},
	menuButton: {
		marginLeft: 2,
		color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b"
	},
}));

const TicketActionButtons = ({ ticket, onRefreshTicket, readOnly = false }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [slaLoading, setSlaLoading] = useState(false);
	const [wabaCallLoading, setWabaCallLoading] = useState(false);
	const [wabaCallConfirmOpen, setWabaCallConfirmOpen] = useState(false);
	const [wabaPermissionDialogOpen, setWabaPermissionDialogOpen] =
		useState(false);
	const [wabaCallStarted, setWabaCallStarted] = useState(false);
	const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
	const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
	const [handoffLoading, setHandoffLoading] = useState(false);
	const [pauseSlaDialogOpen, setPauseSlaDialogOpen] = useState(false);
	const [slaPauseReason, setSlaPauseReason] = useState(
		"Aguardando retorno do cliente"
	);
	const [allowUsersClosePendingWithoutAccept, setAllowUsersClosePendingWithoutAccept] =
		useState(user?.profile === "admin");
	const [resolutionSubject, setResolutionSubject] = useState(ticket?.subject || "");
	const [resolutionSummary, setResolutionSummary] = useState(
		ticket?.resolutionSummary || ""
	);
	const [subjectOptions, setSubjectOptions] = useState([]);
	const [loadingSubjectOptions, setLoadingSubjectOptions] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const isSupportSessionActive = Boolean(user?.supportSession?.active);
	const isAdminLikeUser =
		user?.profile === "admin" ||
		Boolean(user?.supportSession?.active) ||
		Boolean(user?.supervisorMode);
	const operationalUserId = isSupportSessionActive ? undefined : user?.id;

	React.useEffect(() => {
		setResolutionSubject(ticket?.subject || "");
	}, [ticket?.subject, ticket?.id]);

	React.useEffect(() => {
		setResolutionSummary(ticket?.resolutionSummary || "");
	}, [ticket?.resolutionSummary, ticket?.id]);

	React.useEffect(() => {
		setWabaCallStarted(false);
		setWabaPermissionDialogOpen(false);
		setWabaCallConfirmOpen(false);
	}, [ticket?.id]);

	React.useEffect(() => {
		let isMounted = true;

		const loadSubjectOptions = async () => {
			if (!resolveDialogOpen || !ticket?.subjectRequired) {
				return;
			}

			setLoadingSubjectOptions(true);
			try {
				const { data } = await api.get("/ticket-subjects/options");
				if (!isMounted) return;
				setSubjectOptions(Array.isArray(data) ? data : []);
			} catch (err) {
				if (isMounted) {
					setSubjectOptions([]);
				}
				toastError(err);
			} finally {
				if (isMounted) {
					setLoadingSubjectOptions(false);
				}
			}
		};

		loadSubjectOptions();

		return () => {
			isMounted = false;
		};
	}, [resolveDialogOpen, ticket?.subjectRequired]);

	React.useEffect(() => {
		if (ticket?.slaPausedReason) {
			setSlaPauseReason(ticket.slaPausedReason);
			return;
		}
		setSlaPauseReason("Aguardando retorno do cliente");
	}, [ticket?.slaPausedReason, ticket?.id]);

	React.useEffect(() => {
		let isMounted = true;

		const loadClosePendingPermission = async () => {
			if (isAdminLikeUser) {
				if (isMounted) {
					setAllowUsersClosePendingWithoutAccept(true);
				}
				return;
			}

			try {
				const { data } = await api.get("/companies/current/preferences");
				if (!isMounted) {
					return;
				}

				setAllowUsersClosePendingWithoutAccept(
					Boolean(data?.allowUsersClosePendingWithoutAccept)
				);
			} catch (err) {
				if (isMounted) {
					setAllowUsersClosePendingWithoutAccept(false);
				}
			}
		};

		loadClosePendingPermission();

		return () => {
			isMounted = false;
		};
	}, [isAdminLikeUser]);

	const isWabaTicket =
		String(ticket?.whatsapp?.connectionType || "").toLowerCase() === "waba" &&
		Boolean(ticket?.whatsapp?.wabaEnabled);

	// Link de continuacao no WebChat: so na API oficial (waba ou gupshup), com
	// o recurso ligado na conexao. pendingUntil = um link ja foi e espera o
	// cliente abrir.
	const ticketConnectionType = String(ticket?.whatsapp?.connectionType || "").toLowerCase();
	const isOfficialApiTicket =
		ticketConnectionType === "gupshup" ||
		(ticketConnectionType === "waba" && Boolean(ticket?.whatsapp?.wabaEnabled));
	// Mesma regra do backend: chamado com dono so pelo dono (ou administrador).
	const canSendHandoff =
		!ticket?.userId ||
		Number(ticket.userId) === Number(user?.id) ||
		["admin", "superadmin"].includes(String(user?.profile || "").toLowerCase());
	const handoffAvailable =
		isOfficialApiTicket &&
		canSendHandoff &&
		Boolean(ticket?.webchatHandoff?.enabled) &&
		["open", "pending"].includes(String(ticket?.status || "").toLowerCase());
	const handoffPendingUntil = ticket?.webchatHandoff?.pendingUntil
		? new Date(ticket.webchatHandoff.pendingUntil)
		: null;
	const handoffPending = Boolean(
		handoffPendingUntil && handoffPendingUntil.getTime() > Date.now()
	);
	const formatHandoffTime = date => {
		try {
			return new Date(date).toLocaleString("pt-BR", {
				day: "2-digit",
				month: "2-digit",
				hour: "2-digit",
				minute: "2-digit"
			});
		} catch (_error) {
			return "";
		}
	};
	const userCanUseWabaCalls = Boolean(user?.wabaCallingAllowed);
	const wabaCallStatus = String(ticket?.wabaCall?.status || "").toUpperCase();
	const wabaCallPermissionStatus = String(
		ticket?.wabaCallPermission?.status || ""
	).toUpperCase();
	const wabaCallPermission = ticket?.wabaCallPermission || {};
	const wabaPermissionExpiresAt = wabaCallPermission?.expiresAt || null;
	const wabaPermissionNextRequestAt = wabaCallPermission?.nextRequestAt || null;
	const wabaPermissionRequestWindowExpiresAt =
		wabaCallPermission?.requestWindowExpiresAt || null;
	const wabaPermissionRequestsInWindow = Number(
		wabaCallPermission?.requestsInWindow || 0
	);
	const wabaPermissionRequestLimit = Number(
		wabaCallPermission?.requestLimit || 2
	);
	const wabaPermissionCanRequest = Boolean(wabaCallPermission?.canRequest);
	const wabaTemplateRequired = Boolean(ticket?.wabaTemplateRequired);
	const wabaPermissionCanRequestNow =
		wabaPermissionCanRequest && !wabaTemplateRequired;
	const formatWabaDate = value => {
		if (!value) return "";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return "";
		return date.toLocaleString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		});
	};
	const wabaPermissionExpiresAtText = formatWabaDate(wabaPermissionExpiresAt);
	const wabaPermissionNextRequestAtText = formatWabaDate(
		wabaPermissionNextRequestAt
	);
	const wabaPermissionWindowExpiresAtText = formatWabaDate(
		wabaPermissionRequestWindowExpiresAt
	);
	const wabaPermissionIsGranted = ["GRANTED", "PERMANENT", "TEMPORARY"].includes(
		wabaCallPermissionStatus
	);
	const wabaPermissionIsDeclined = wabaCallPermissionStatus === "DECLINED";
	const wabaPermissionIsRateLimited =
		wabaCallPermissionStatus === "RATE_LIMITED";
	const wabaPermissionIsRequested = wabaCallPermissionStatus === "REQUESTED";
	const wabaPermissionStatusLabel = (() => {
		if (wabaPermissionIsGranted) {
			return wabaPermissionExpiresAtText
				? i18n.t("ticketActions.wabaCall.permissionStatus.grantedTemporary")
				: i18n.t("ticketActions.wabaCall.permissionStatus.grantedPermanent");
		}
		if (wabaPermissionIsDeclined) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.declined");
		}
		if (wabaPermissionIsRateLimited) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.rateLimited");
		}
		if (wabaPermissionIsRequested) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.requested");
		}
		return i18n.t("ticketActions.wabaCall.permissionStatus.noPermission");
	})();
	const wabaPermissionStatusHelp = (() => {
		if (wabaPermissionIsGranted) {
			return wabaPermissionExpiresAtText
				? i18n.t("ticketActions.wabaCall.permissionStatus.validUntil", {
						date: wabaPermissionExpiresAtText
				  })
				: i18n.t("ticketActions.wabaCall.permissionStatus.permanentHelp");
		}
		if (wabaPermissionIsDeclined) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.declinedHelp");
		}
		if (wabaPermissionIsRateLimited) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.rateLimitedHelp");
		}
		if (wabaPermissionIsRequested) {
			return i18n.t("ticketActions.wabaCall.permissionStatus.requestedHelp");
		}
		return i18n.t("ticketActions.wabaCall.permissionStatus.noPermissionHelp");
	})();
	const wabaPermissionBadgeTone = wabaPermissionIsGranted
		? classes.wabaPermissionBadgeSuccess
		: wabaPermissionIsDeclined || wabaPermissionIsRateLimited
		? classes.wabaPermissionBadgeDanger
		: wabaPermissionIsRequested
		? classes.wabaPermissionBadgeWarning
		: classes.wabaPermissionBadgeNeutral;
	const isWabaCallActive =
		wabaCallStarted ||
		["RINGING", "ACCEPTED", "IN_PROGRESS", "ONGOING"].includes(
			wabaCallStatus
		);
	const userSipStatus = String(user?.sipStatus || "").toLowerCase();
	const userHasUnavailableSip =
		user?.sipEnabled === false ||
		userSipStatus === "offline" ||
		userSipStatus === "unavailable";
	const wabaCallAction = (() => {
		if (!isWabaTicket || !userCanUseWabaCalls) {
			return null;
		}

		if (isWabaCallActive) {
			return {
				disabled: true,
				helper: i18n.t("ticketActions.wabaCall.inCallHelp"),
				icon: <PhoneInTalk />,
				label: i18n.t("ticketActions.wabaCall.inCall")
			};
		}

		if (userHasUnavailableSip) {
			return {
				action: "unavailable",
				disabled: true,
				helper: i18n.t("ticketActions.wabaCall.extensionUnavailableHelp"),
				icon: <PhoneDisabled />,
				label: i18n.t("ticketActions.wabaCall.extensionUnavailable")
			};
		}

		if (wabaCallPermissionStatus === "GRANTED") {
			return {
				action: "call",
				disabled: false,
				helper: wabaPermissionExpiresAt
					? `${i18n.t("ticketActions.wabaCall.callHelp")} ${i18n.t(
							"ticketActions.wabaCall.validUntil"
					  )} ${formatWabaDate(wabaPermissionExpiresAt)}.`
					: i18n.t("ticketActions.wabaCall.callHelp"),
				icon: <Phone />,
				label: i18n.t("ticketActions.wabaCall.call")
			};
		}

		return {
			action: "permission",
			disabled: false,
			helper: wabaTemplateRequired
				? i18n.t("ticketActions.wabaCall.templateRequiredHelp")
				: i18n.t("ticketActions.wabaCall.openPermissionHelp"),
			icon: <Phone />,
			label: i18n.t("ticketActions.wabaCall.call")
		};
	})();

	const handleWabaCallAction = async () => {
		if (wabaCallAction?.action === "call") {
			setWabaCallConfirmOpen(true);
			return;
		}

		if (wabaCallAction?.action === "permission") {
			setWabaPermissionDialogOpen(true);
			return;
		}
	};

	const handleRequestWabaCallPermission = async () => {
		if (wabaTemplateRequired) {
			toast.warn(i18n.t("ticketActions.wabaCall.templateRequiredHelp"));
			return;
		}

		if (!wabaPermissionCanRequest) {
			toast.warn(i18n.t("ticketActions.wabaCall.permissionStatus.requestBlocked"));
			return;
		}

		setWabaCallLoading(true);
		try {
			const { data } = await api.post(
				`/tickets/${ticket.id}/waba/call-permission/request`
			);
			if (String(data?.status || "").toUpperCase() === "GRANTED") {
				toast.success(i18n.t("ticketActions.wabaCall.permissionConfirmed"));
				setWabaPermissionDialogOpen(false);
				setWabaCallConfirmOpen(true);
			} else if (String(data?.status || "").toUpperCase() === "DECLINED") {
				toast.warn(i18n.t("ticketActions.wabaCall.permissionDeclined"));
			} else if (String(data?.status || "").toUpperCase() === "RATE_LIMITED") {
				toast.warn(i18n.t("ticketActions.wabaCall.permissionRateLimited"));
			} else {
				toast.success(i18n.t("ticketActions.wabaCall.requestSent"));
			}
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setWabaCallLoading(false);
		}
	};

	const handleCloseWabaCallConfirm = () => {
		if (wabaCallLoading) {
			return;
		}

		setWabaCallConfirmOpen(false);
	};

	const handleCloseWabaPermissionDialog = () => {
		if (wabaCallLoading) {
			return;
		}

		setWabaPermissionDialogOpen(false);
	};

	const handleStartWabaOutboundCall = async () => {
		setWabaCallLoading(true);
		try {
			await api.post(`/tickets/${ticket.id}/waba/call/start`);
			setWabaCallStarted(true);
			setWabaCallConfirmOpen(false);
			toast.success(i18n.t("ticketActions.wabaCall.callStarted"));
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setWabaCallLoading(false);
		}
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (
		e,
		status,
		userId,
		extraData = {}
	) => {
		setLoading(true);
		try {
			const payload = {
				status,
				...extraData
			};

			if (typeof userId !== "undefined") {
				payload.userId = userId;
			}

			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, payload);
			window.dispatchEvent(
				new CustomEvent("ticket:updated", {
					detail: {
						ticket: updatedTicket,
						previousStatus: ticket?.status,
						requestedStatus: status
					}
				})
			);
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleSendWebchatHandoff = async () => {
		setHandoffLoading(true);
		try {
			const { data } = await api.post(`/tickets/${ticket.id}/webchat-handoff`, {
				resend: handoffPending
			});
			if (data?.alreadySent) {
				toast.info(
					`O link já tinha sido enviado e vale até ${formatHandoffTime(data.expiresAt)}.`
				);
			} else {
				toast.success("Link do WebChat enviado ao cliente.");
			}
			setHandoffDialogOpen(false);
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setHandoffLoading(false);
		}
	};

	const handleOpenResolveDialog = () => {
		setResolutionSubject(ticket?.subject || "");
		setResolutionSummary(ticket?.resolutionSummary || "");
		setResolveDialogOpen(true);
	};

	const handleCloseResolveDialog = () => {
		if (loading) {
			return;
		}
		setResolveDialogOpen(false);
	};

	const handleResolveTicket = e =>
		handleUpdateTicketStatus(e, "closed", operationalUserId, {
			subject: resolutionSubject,
			resolutionSummary
		});

	const isAllowedResolutionSubject = !ticket?.subjectRequired
		? true
		: subjectOptions.length === 0
		? false
		: subjectOptions.some(
				item =>
					String(item?.name || "").trim().toLowerCase() ===
					String(resolutionSubject || "").trim().toLowerCase()
		  );

	const handleOpenPauseSlaDialog = () => {
		setPauseSlaDialogOpen(true);
	};

	const handleClosePauseSlaDialog = () => {
		if (slaLoading) {
			return;
		}
		setPauseSlaDialogOpen(false);
	};

	const handleUpdateSla = async action => {
		setSlaLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}/sla`, {
				action,
				...(action === "pause" ? { reason: slaPauseReason } : {})
			});
			setPauseSlaDialogOpen(false);
			if (typeof onRefreshTicket === "function") {
				onRefreshTicket();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setSlaLoading(false);
		}
	};

	const isSlaPaused = Boolean(ticket?.slaPausedAt);
	const canClosePendingWithoutAccept =
		isAdminLikeUser || allowUsersClosePendingWithoutAccept;

	if (readOnly) {
		return null;
	}

	return (
		<div className={classes.actionButtons}>
			<Dialog
				open={wabaCallConfirmOpen}
				onClose={handleCloseWabaCallConfirm}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>{i18n.t("ticketActions.wabaCall.confirmTitle")}</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary">
						{i18n.t("ticketActions.wabaCall.confirmBody")}
					</Typography>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleCloseWabaCallConfirm}
						color="default"
					>
						{i18n.t("ticketActions.wabaCall.confirmCancel")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={wabaCallLoading}
						onClick={handleStartWabaOutboundCall}
						color="primary"
						variant="contained"
					>
						{i18n.t("ticketActions.wabaCall.confirmStart")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			<Dialog
				open={wabaPermissionDialogOpen}
				onClose={handleCloseWabaPermissionDialog}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>{i18n.t("ticketActions.wabaCall.permissionTitle")}</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						{wabaTemplateRequired
							? i18n.t("ticketActions.wabaCall.templateRequiredHelp")
							: wabaCallPermissionStatus === "DECLINED"
							? `${i18n.t("ticketActions.wabaCall.declinedHelp")} ${
									wabaPermissionNextRequestAt
										? `${i18n.t("ticketActions.wabaCall.nextRequestAt")} ${formatWabaDate(
												wabaPermissionNextRequestAt
										  )}.`
										: ""
							  }`
							: wabaCallPermissionStatus === "RATE_LIMITED"
							? `${i18n.t("ticketActions.wabaCall.rateLimitedHelp")} ${
									wabaPermissionNextRequestAt
										? `${i18n.t("ticketActions.wabaCall.nextRequestAt")} ${formatWabaDate(
												wabaPermissionNextRequestAt
										  )}.`
										: ""
							  }`
							: i18n.t("ticketActions.wabaCall.permissionBody")}
					</Typography>
					<div className={classes.wabaPermissionStatusCard}>
						<div className={classes.wabaPermissionStatusHeader}>
							<Typography variant="subtitle2">
								{i18n.t("ticketActions.wabaCall.permissionStatus.title")}
							</Typography>
							<span
								className={`${classes.wabaPermissionBadge} ${wabaPermissionBadgeTone}`}
							>
								{wabaPermissionStatusLabel}
							</span>
						</div>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{wabaPermissionStatusHelp}
						</Typography>
						<div className={classes.wabaPermissionMetaRow}>
							<Typography variant="caption" color="textSecondary">
								{i18n.t("ticketActions.wabaCall.permissionStatus.requestsUsed")}
							</Typography>
							<Typography
								variant="caption"
								className={classes.wabaPermissionMetaValue}
							>
								{i18n.t("ticketActions.wabaCall.permissionStatus.requestsCount", {
									count: wabaPermissionRequestsInWindow,
									limit: wabaPermissionRequestLimit
								})}
							</Typography>
						</div>
						{wabaPermissionWindowExpiresAtText && (
							<div className={classes.wabaPermissionMetaRow}>
								<Typography variant="caption" color="textSecondary">
									{i18n.t("ticketActions.wabaCall.permissionStatus.window")}
								</Typography>
								<Typography
									variant="caption"
									className={classes.wabaPermissionMetaValue}
								>
									{wabaPermissionWindowExpiresAtText}
								</Typography>
							</div>
						)}
						{wabaPermissionNextRequestAtText && (
							<div className={classes.wabaPermissionMetaRow}>
								<Typography variant="caption" color="textSecondary">
									{i18n.t("ticketActions.wabaCall.permissionStatus.nextRequest")}
								</Typography>
								<Typography
									variant="caption"
									className={classes.wabaPermissionMetaValue}
								>
									{wabaPermissionNextRequestAtText}
								</Typography>
							</div>
						)}
						<div className={classes.wabaPermissionMetaRow}>
							<Typography variant="caption" color="textSecondary">
								{i18n.t("ticketActions.wabaCall.permissionStatus.action")}
							</Typography>
							<Typography
								variant="caption"
								className={classes.wabaPermissionMetaValue}
							>
								{wabaPermissionIsGranted
									? i18n.t("ticketActions.wabaCall.permissionStatus.callAllowed")
									: wabaPermissionCanRequestNow
									? i18n.t("ticketActions.wabaCall.permissionStatus.requestAllowed")
									: i18n.t("ticketActions.wabaCall.permissionStatus.requestBlocked")}
							</Typography>
						</div>
					</div>
					<Typography variant="caption" color="textSecondary">
						{i18n.t("ticketActions.wabaCall.permissionRules", {
							count: wabaPermissionRequestsInWindow,
							limit: wabaPermissionRequestLimit
						})}
					</Typography>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleCloseWabaPermissionDialog}
						color="default"
					>
						{i18n.t("ticketActions.wabaCall.confirmCancel")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={wabaCallLoading}
						onClick={handleRequestWabaCallPermission}
						color="primary"
						variant="contained"
						disabled={!wabaPermissionCanRequestNow}
					>
						{i18n.t("ticketActions.wabaCall.requestPermission")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			<Dialog
				open={resolveDialogOpen}
				onClose={handleCloseResolveDialog}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Resolver chamado</DialogTitle>
				<DialogContent dividers>
					{handoffPending ? (
						<Typography variant="body2" color="error" gutterBottom>
							O cliente ainda não abriu o link do WebChat (vale até{" "}
							{formatHandoffTime(handoffPendingUntil)}). Não é preciso resolver: este
							chamado fecha sozinho, sem mensagem, quando ele abrir o link. Resolvendo
							agora, a despedida e a pesquisa de satisfação saem pelo WhatsApp e são
							cobradas.
						</Typography>
					) : null}
					{ticket?.subjectRequired || !handoffPending ? (
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Informe o assunto para encerrar este chamado. O resumo é opcional.
						</Typography>
					) : null}
					{ticket?.subjectRequired ? (
						<FormControl fullWidth variant="outlined" margin="dense">
							<InputLabel id="resolve-ticket-subject-label">
								Assunto do chamado
							</InputLabel>
							<Select
								labelId="resolve-ticket-subject-label"
								label="Assunto do chamado"
								value={resolutionSubject}
								onChange={e => setResolutionSubject(e.target.value)}
								disabled={loadingSubjectOptions}
							>
								<MenuItem value="">
									<em>
										{loadingSubjectOptions
											? "Carregando assuntos..."
											: "Selecione o assunto"}
									</em>
								</MenuItem>
								{subjectOptions.map(item => (
									<MenuItem key={item.id} value={item.name}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : (
						<TextField
							fullWidth
							variant="outlined"
							label="Assunto do chamado"
							value={resolutionSubject}
							onChange={e => setResolutionSubject(e.target.value)}
							margin="dense"
						/>
					)}
					{ticket?.subjectRequired && subjectOptions.length === 0 && !loadingSubjectOptions ? (
						<Typography variant="body2" color="error" gutterBottom>
							Nenhum assunto cadastrado. Cadastre em "Assuntos do Chamado" para encerrar tickets.
						</Typography>
					) : null}
					<TextField
						fullWidth
						variant="outlined"
						label="Breve resumo"
						value={resolutionSummary}
						onChange={e => setResolutionSummary(e.target.value)}
						margin="dense"
						multiline
						minRows={4}
					/>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleCloseResolveDialog}
						color="default"
					>
						Cancelar
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						onClick={handleResolveTicket}
						color="primary"
						variant="contained"
						disabled={
							loadingSubjectOptions ||
							(ticket?.subjectRequired && !isAllowedResolutionSubject)
						}
					>
						Resolver
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			<Dialog
				open={pauseSlaDialogOpen}
				onClose={handleClosePauseSlaDialog}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Pausar SLA</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Informe o motivo da pausa para auditoria de atendimento.
					</Typography>
					<TextField
						fullWidth
						variant="outlined"
						label="Motivo da pausa"
						value={slaPauseReason}
						onChange={e => setSlaPauseReason(e.target.value)}
						margin="dense"
						multiline
						minRows={3}
					/>
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={handleClosePauseSlaDialog}
						color="default"
					>
						Cancelar
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={slaLoading}
						onClick={() => handleUpdateSla("pause")}
						color="primary"
						variant="contained"
					>
						Pausar SLA
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			{wabaCallAction && (
				<Tooltip arrow title={wabaCallAction.helper}>
					<span>
						<ButtonWithSpinner
							loading={wabaCallLoading}
							startIcon={wabaCallAction.icon}
							size="small"
							variant="outlined"
							className={classes.softButton}
							disabled={
								wabaCallAction.disabled || loading || slaLoading || wabaCallLoading
							}
							onClick={handleWabaCallAction}
						>
							{wabaCallAction.label}
						</ButtonWithSpinner>
					</span>
				</Tooltip>
			)}
			{handoffAvailable && (
				<Tooltip
					arrow
					title={
						ticket?.wabaTemplateRequired
							? "A janela de 24h do WhatsApp está fechada: o link só pode ser enviado depois que o cliente mandar uma mensagem."
							: handoffPending
							? `Link enviado, vale até ${formatHandoffTime(handoffPendingUntil)}. O chamado fecha sozinho quando o cliente abrir.`
							: "Enviar ao cliente um link para continuar esta conversa no WebChat, onde as mensagens não são cobradas."
					}
				>
					<span>
						<ButtonWithSpinner
							loading={handoffLoading}
							startIcon={<Forum />}
							size="small"
							variant="outlined"
							className={classes.softButton}
							disabled={Boolean(ticket?.wabaTemplateRequired) || loading || handoffLoading}
							onClick={() => setHandoffDialogOpen(true)}
						>
							{handoffPending
								? `Link até ${formatHandoffTime(handoffPendingUntil)}`
								: "Enviar para o WebChat"}
						</ButtonWithSpinner>
					</span>
				</Tooltip>
			)}
			<Dialog
				open={handoffDialogOpen}
				onClose={() => !handoffLoading && setHandoffDialogOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>
					{handoffPending ? "Reenviar link do WebChat" : "Continuar no WebChat"}
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" gutterBottom>
						O cliente recebe no WhatsApp um link pessoal para continuar esta conversa
						no WebChat, onde as mensagens não são cobradas pela Meta. O link custa uma
						mensagem.
					</Typography>
					<Typography variant="body2" gutterBottom>
						{ticket?.userId
							? "Quando o cliente abrir, este chamado fecha sem mensagem nenhuma e um chamado no WebChat abre com o mesmo atendente."
							: "Quando o cliente abrir, este chamado fecha sem mensagem nenhuma e um chamado no WebChat abre na fila, sem atendente. Para seguir com você, aceite o chamado antes de enviar o link."}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						No WebChat ainda não dá para enviar nem receber fotos, áudios ou
						arquivos. Envie boletos e comprovantes antes, ou use links e o PIX copia e
						cola.
					</Typography>
					{handoffPending ? (
						<Typography variant="body2" color="error" style={{ marginTop: 12 }}>
							Já existe um link enviado, válido até{" "}
							{formatHandoffTime(handoffPendingUntil)}. Reenviar manda uma nova
							mensagem (cobrada) e o link anterior deixa de funcionar.
						</Typography>
					) : null}
				</DialogContent>
				<DialogActions>
					<ButtonWithSpinner
						loading={false}
						onClick={() => setHandoffDialogOpen(false)}
						disabled={handoffLoading}
					>
						Cancelar
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={handoffLoading}
						variant="contained"
						color="primary"
						disabled={handoffLoading}
						onClick={handleSendWebchatHandoff}
					>
						{handoffPending ? "Reenviar link" : "Enviar link"}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading || slaLoading}
					startIcon={<Replay />}
					size="small"
					variant="outlined"
					className={classes.softButton}
					onClick={e => handleUpdateTicketStatus(e, "open", operationalUserId)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{isSlaPaused ? (
						<Tooltip arrow title="Retomar SLA">
							<span>
								<IconButton
									size="small"
									className={classes.actionIconButton}
									onClick={() => handleUpdateSla("resume")}
									disabled={loading || slaLoading}
								>
									<PlayArrow fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					) : (
						<Tooltip arrow title="Pausar SLA">
							<span>
								<IconButton
									size="small"
									className={classes.actionIconButton}
									onClick={handleOpenPauseSlaDialog}
									disabled={loading || slaLoading}
								>
									<PauseCircleOutline fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip arrow title={i18n.t("messagesList.header.buttons.return")}>
						<span>
							<IconButton
								size="small"
								className={classes.actionIconButton}
								onClick={e => handleUpdateTicketStatus(e, "pending", null)}
								disabled={loading || slaLoading}
							>
								<Replay fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip arrow title={i18n.t("messagesList.header.buttons.resolve")}>
						<span>
							<IconButton
								size="small"
								className={classes.actionIconButtonPrimary}
								onClick={e =>
									ticket.subjectRequired || handoffPending
										? handleOpenResolveDialog()
										: handleUpdateTicketStatus(e, "closed", operationalUserId)
								}
								disabled={loading || slaLoading}
							>
								<CheckCircleOutline fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<IconButton
						size="small"
						className={classes.menuButton}
						onClick={handleOpenTicketOptionsMenu}
					>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						onRefreshTicket={onRefreshTicket}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					<ButtonWithSpinner
						loading={loading || slaLoading}
						size="small"
						variant="contained"
						color="primary"
						className={classes.resolveButton}
						onClick={e => handleUpdateTicketStatus(e, "open", operationalUserId)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					{canClosePendingWithoutAccept && (
						<ButtonWithSpinner
							loading={loading || slaLoading}
							size="small"
							variant="outlined"
							className={classes.softButton}
							onClick={e =>
								ticket.subjectRequired || handoffPending
									? handleOpenResolveDialog()
									: handleUpdateTicketStatus(e, "closed", operationalUserId)
							}
						>
							{i18n.t("messagesList.header.buttons.resolve")}
						</ButtonWithSpinner>
					)}
				</>
			)}
		</div>
	);
};

export default TicketActionButtons;
